/**
 * Campaign Performance report data + filter options.
 *
 * The report does NOT fetch on mount, same as Team/Agent Performance: it runs
 * only when `run()` is called with a validated filter set, because the query
 * fans across the conversations table and an unbounded default window would be
 * an expensive query nobody asked for. `hasRun` drives the empty state.
 */
import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client.js'
import { buildReportParams, type CampaignReportFilters } from '../components/reports/report-query.js'

export type { CampaignReportFilters } from '../components/reports/report-query.js'

export interface CampaignPerformanceRow {
  bucketStart: string
  campaignId: string
  campaignName: string
  total: number
  newCount: number
  assigned: number
  unassigned: number
  resolved: number
  reOpened: number
  transferred: number
  primary: number
  mergedAsSecondary: number
  avgWaitTime: number
  withinResponseSla: number
  withinResolvedSla: number
  responseEscalated: number
  resolutionEscalated: number
  avgResponseTime: number
  avgResolutionTime: number
  responseSlaPct: number
  resolutionSlaPct: number
}

export interface FilterOption { label: string; value: string }
export interface TimezoneOption extends FilterOption { offset: string }

interface FilterOptions {
  timezones: TimezoneOption[]
  campaigns: FilterOption[]
}

/** Options for the filter panel — timezones + the campaign picklist. */
export function useCampaignPerformanceFilterOptions(): {
  options: FilterOptions
  loading: boolean
} {
  const [options, setOptions] = useState<FilterOptions>({
    timezones: [], campaigns: [],
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      setLoading(true)
      try {
        const res = await api.get<FilterOptions>(
          '/reports/conversations/campaign-performance/filters',
        )
        if (!cancelled) setOptions(res)
      } catch {
        if (!cancelled) setOptions({ timezones: [], campaigns: [] })
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  return { options, loading }
}

interface UseReportResult {
  rows: CampaignPerformanceRow[]
  loading: boolean
  error: string | null
  hasRun: boolean
  run: (filters: CampaignReportFilters) => Promise<void>
  reset: () => void
}

export function useCampaignPerformanceReport(): UseReportResult {
  const [rows, setRows] = useState<CampaignPerformanceRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasRun, setHasRun] = useState(false)

  const run = useCallback(async (filters: CampaignReportFilters): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<{ rows: CampaignPerformanceRow[] }>(
        `/reports/conversations/campaign-performance?${buildReportParams(filters).toString()}`,
      )
      setRows(res.rows ?? [])
      setHasRun(true)
    } catch (err) {
      setRows([])
      setHasRun(true)
      setError(err instanceof Error ? err.message : 'Could not load the report.')
    } finally {
      setLoading(false)
    }
  }, [])

  const reset = useCallback((): void => {
    setRows([])
    setHasRun(false)
    setError(null)
  }, [])

  return { rows, loading, error, hasRun, run, reset }
}

export interface CampaignTimelineRow {
  campaignId: string
  campaignName: string
  openedAt: string | null
  statusName: string
  assigned: boolean
  transferred: boolean
  mergedAsSecondary: boolean
}

/**
 * Raw per-conversation rows for the Analytics view's volume-by-hour heatmap —
 * the aggregated report has no timestamps to draw it from. Fetched lazily: pass
 * the SAME filters the aggregated report was run with only once the Analytics
 * tab is actually opened, so switching to it the first time costs one extra
 * request instead of every report run costing two.
 */
export function useCampaignPerformanceTimeline(): {
  rows: CampaignTimelineRow[]
  loading: boolean
  truncated: boolean
  /** True once a fetch has completed (or failed) — guards against re-fetching on every tab toggle. */
  hasFetched: boolean
  fetch: (filters: CampaignReportFilters) => Promise<void>
  reset: () => void
} {
  const [rows, setRows] = useState<CampaignTimelineRow[]>([])
  const [loading, setLoading] = useState(false)
  const [truncated, setTruncated] = useState(false)
  const [hasFetched, setHasFetched] = useState(false)

  const fetchTimeline = useCallback(async (filters: CampaignReportFilters): Promise<void> => {
    setLoading(true)
    try {
      const res = await api.get<{ rows: CampaignTimelineRow[]; meta: { truncated: boolean } }>(
        `/reports/conversations/campaign-performance/timeline?${buildReportParams(filters).toString()}`,
      )
      setRows(res.rows ?? [])
      setTruncated(res.meta?.truncated ?? false)
    } catch {
      setRows([])
      setTruncated(false)
    } finally {
      setLoading(false)
      setHasFetched(true)
    }
  }, [])

  const reset = useCallback((): void => {
    setRows([])
    setTruncated(false)
    setHasFetched(false)
  }, [])

  return { rows, loading, truncated, hasFetched, fetch: fetchTimeline, reset }
}
